import { type CSSProperties } from "react";
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconDotsVertical, IconTrash, IconUpload } from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  getDfySelectionNumbers,
  type DfyOrderImageItem,
} from "@/lib/stores/dfy-order-store/dfy-order-selectors";
import { useDfyImageDropzone } from "./useDfyImageDropzone";
import { ImageOrderBadge } from "./ImageMarkers";

export default function ImageGrid({
  items,
  selectable,
  canAdd,
  onToggleSelect,
  onRemove,
  onReorder,
  onAdd,
}: {
  items: DfyOrderImageItem[];
  selectable: boolean;
  canAdd: boolean;
  onToggleSelect: (externalId: string) => void;
  onRemove: (externalId: string) => void;
  onReorder: (activeId: string, overId: string) => void;
  onAdd: (files: File[]) => void;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
  );

  const { getRootProps, getInputProps, isDragActive, open } =
    useDfyImageDropzone(onAdd, {
      disabled: !canAdd,
      noClick: true,
      noKeyboard: true,
    });

  const selectionNumbers = getDfySelectionNumbers(items);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!selectable || !over || active.id === over.id) {
      return;
    }
    onReorder(String(active.id), String(over.id));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll={{ threshold: { x: 0, y: 0.2 } }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.config.externalId)}
        strategy={rectSortingStrategy}
      >
        <div className="relative" {...getRootProps()}>
          <input {...getInputProps()} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {items.map((item) => (
              <SortableImageThumb
                key={item.config.externalId}
                externalId={item.config.externalId}
                src={item.image.thumbnailSrc}
                selectable={selectable}
                isSelected={item.config.isSelected}
                orderNumber={
                  selectable
                    ? (selectionNumbers.get(item.config.externalId) ?? null)
                    : null
                }
                onToggleSelect={onToggleSelect}
                onRemove={onRemove}
              />
            ))}
            {canAdd && (
              <button
                type="button"
                onClick={open}
                className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-file-upload px-2 [&_svg]:stroke-[url(#icon-gradient)]"
              >
                <IconUpload stroke={2} size={20} />
                <div className="h-1.5" />
                <p className="text-center text-xs text-text-secondary">
                  Add more images
                </p>
              </button>
            )}
          </div>
          {isDragActive && (
            <div className="pointer-events-none absolute -inset-2 z-20 flex flex-col items-center justify-center rounded-xl border border-dashed border-primary-400 bg-primary-100/90 [&_svg]:stroke-[url(#icon-gradient)]">
              <IconUpload stroke={2} size={24} />
              <div className="h-2" />
              <p className="text-sm text-text-secondary">Drop images to add</p>
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableImageThumb({
  externalId,
  src,
  selectable,
  isSelected,
  orderNumber,
  onToggleSelect,
  onRemove,
}: {
  externalId: string;
  src: string;
  selectable: boolean;
  isSelected: boolean;
  orderNumber: number | null;
  onToggleSelect: (externalId: string) => void;
  onRemove: (externalId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: externalId });
  const isDragging = selectable && isSortableDragging;

  const style: CSSProperties = selectable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
      }
    : {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        disabled={!selectable}
        onClick={() => onToggleSelect(externalId)}
        className={`relative block w-full overflow-hidden rounded-lg ${
          selectable ? "cursor-pointer" : ""
        }`}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="aspect-square w-full object-cover select-none [-webkit-touch-callout:none]"
        />
        {selectable && !isDragging && (
          <ImageOrderBadge orderNumber={orderNumber} isSelected={isSelected} />
        )}
      </button>
      {!isDragging && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="iconXs"
              aria-label="Image options"
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            >
              <IconDotsVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={(event) => event.preventDefault()}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            <DropdownMenuItem
              className="text-error"
              onSelect={() => onRemove(externalId)}
            >
              <IconTrash />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
